import React, { useContext, useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { LayoutContext } from "../../context/LayoutContext";
import BaseWrapper from "../../components/ui/BaseWrapper";
import BaseSelect from "../../components/ui/BaseSelect";
import DateRangeShortcutPicker from "../../components/ui/DateRangeShortcutPicker";
import { useAnalyticsLiveRefresh } from "../../hooks/useAnalyticsLiveRefresh";

import KpiCards from "./KpiCards";
import TargetProgress from "./TargetProgress";
import WorkOrderDonut from "./WorkOrderDonut";
import YearAnalysisDonut from "./YearAnalysisDonut";
import StatusFunnel from "./StatusFunnel";
import TeamTrendsChart from "./TeamTrendsChart";
import ClientTrendsChart from "./ClientTrendsChart";
import EngineersSection from "./EngineersSection";
import RevenuePanel from "./RevenuePanel";
import ScheduleTypeChart from "./ScheduleTypeChart";
import PaymentTypeChart from "./PaymentTypeChart";
import DayOfWeekChart from "./DayOfWeekChart";
import TopCategoriesChart from "./TopCategoriesChart";
import WorldMapChart from "../../components/ui/WorldMapChart";
import CountryRanking from "../../components/ui/CountryRanking";
import AdvancedStatsRow from "./AdvancedStatsRow";
import HourlyActivityChart from "./HourlyActivityChart";
import MonthlyCompletionChart from "./MonthlyCompletionChart";
import WorkOrderAgingChart from "./WorkOrderAgingChart";
import SatisfactionBreakdown from "./SatisfactionBreakdown";
import WorkOrderSizeChart from "./WorkOrderSizeChart";
import RemoteOnsiteChart from "./RemoteOnsiteChart";
import CurrencyChart from "./CurrencyChart";
import TopCitiesChart from "./TopCitiesChart";
import CancellationAnalysis from "./CancellationAnalysis";
import ClientYearTrendChart from "./ClientYearTrendChart";

import {
  fetchFilterOptions,
  fetchKpiMetrics,
  fetchAnnualTarget,
  fetchWorkOrderAnalytics,

  fetchBreakdownAnalytics,
  fetchWorldMap,
  fetchAdvancedAnalytics,
} from "../../services/api/analytics";

const ALL_VALUE = "";


const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const now = new Date();
const defaultStart = toDateStr(new Date(now.getFullYear(), now.getMonth(), 1));
const defaultEnd = toDateStr(new Date(now.getFullYear(), now.getMonth() + 1, 0));

const AnimateOnScroll = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.08 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(28px)",
        transition: `opacity 0.5s ease ${delay}ms, transform 0.5s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

const WeekBadge = ({ data }) => {
  if (!data) return null;
  const { thisWeek, lastWeek, changePercent } = data;
  const up = changePercent >= 0;
  return (
    <div className="flex items-center gap-3">
      <div className="text-right hidden sm:block">
        <p className="text-[11px] text-gray-400 leading-none">Last week</p>
        <p className="text-sm font-bold text-gray-700 leading-none mt-0.5">{lastWeek}</p>
      </div>
      <div className="h-6 w-px bg-gray-200 hidden sm:block" />
      <div className="text-right hidden sm:block">
        <p className="text-[11px] text-gray-400 leading-none">This week</p>
        <p className="text-sm font-bold text-gray-700 leading-none mt-0.5">{thisWeek}</p>
      </div>
      <span
        className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full ${
          up ? "bg-green-50 text-green-600" : "bg-red-50 text-red-500"
        }`}
      >
        <i className={`fa-solid ${up ? "fa-arrow-up" : "fa-arrow-down"} text-[9px]`} />
        {Math.abs(changePercent)}% week
      </span>
    </div>
  );
};

const Card = ({ title, subtitle, children, className = "" }) => (
  <div className={`bg-white rounded-2xl border border-gray-100 shadow-sm p-5 ${className}`}>
    {(title || subtitle) && (
      <div className="mb-3">
        {title && <h4 className="text-sm font-semibold text-gray-700">{title}</h4>}
        {subtitle && <p className="text-[11px] text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
    )}
    {children}
  </div>
);

const WorkOrdersAnalytics = () => {
  const { isBannerVisible } = useContext(LayoutContext);
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [clientId, setClientId] = useState(ALL_VALUE);
  const [teamId, setTeamId] = useState(ALL_VALUE);

  // Refetch every analytics-* query the moment a work order changes anywhere.
  useAnalyticsLiveRefresh();

  const { data: filterOptionsResult } = useQuery({
    queryKey: ["analytics-filterOptions"],
    queryFn: fetchFilterOptions,
    staleTime: 300_000,
  });

  const filterOptions = filterOptionsResult?.data;

  const clientOptions = [
    { value: ALL_VALUE, label: "All Clients" },
    ...(filterOptions?.clients?.map((c) => ({ value: String(c.id), label: c.clientName })) ?? []),
  ];

  const teamOptions = [
    { value: ALL_VALUE, label: "All Teams" },
    ...(filterOptions?.teams?.map((t) => ({ value: t.id, label: t.name })) ?? []),
  ];

  const filterParams = {
    ...(clientId ? { clientId } : {}),
    ...(teamId ? { teamId } : {}),
    ...(startDate ? { startDate } : {}),
    ...(endDate ? { endDate } : {}),
  };

  const { data: kpiResult } = useQuery({
    queryKey: ["analytics-kpi", clientId, teamId],
    queryFn: () => fetchKpiMetrics({ clientId, teamId }),
    staleTime: 0,
  });

  const { data: targetResult } = useQuery({
    queryKey: ["analytics-target", clientId, teamId],
    queryFn: () => fetchAnnualTarget(filterParams),
    staleTime: 0,
  });

  const { data: workOrderResult } = useQuery({
    queryKey: ["analytics-workOrders", startDate, endDate, clientId, teamId],
    queryFn: () => fetchWorkOrderAnalytics(filterParams),
    staleTime: 0,
  });

const { data: breakdownResult } = useQuery({
    queryKey: ["analytics-breakdown", startDate, endDate, clientId, teamId],
    queryFn: () => fetchBreakdownAnalytics(filterParams),
    staleTime: 0,
  });

  const { data: worldMapResult } = useQuery({
    queryKey: ["analytics-worldMap", startDate, endDate, clientId, teamId],
    queryFn: () => fetchWorldMap(filterParams),
    staleTime: 0,
  });

  const { data: advancedResult } = useQuery({
    queryKey: ["analytics-advanced", startDate, endDate, clientId, teamId],
    queryFn: () => fetchAdvancedAnalytics(filterParams),
    staleTime: 0,
  });

  const kpi = kpiResult?.data;
  const target = targetResult?.data;
  const analytics = workOrderResult?.data;
  const breakdown = breakdownResult?.data;
  const worldMap = worldMapResult?.data;
  const advanced = advancedResult?.data;

  const durationLabel =
    startDate && endDate
      ? `${startDate} – ${endDate}`
      : startDate
        ? `From ${startDate}`
        : endDate
          ? `Until ${endDate}`
          : "All time";

  const activeFilterCount = [clientId, teamId].filter(Boolean).length;

  return (
    <div
      className={`bg-[#F6F7FB] sm:fixed sticky 2xl:w-[95.2%] xl:w-[93.2%] lg:w-[90.6%] md:w-[87.5%] sm:w-[95%] w-full overflow-y-auto transition-all ${
        isBannerVisible ? "top-[106px] h-[calc(100vh-106px)]" : "top-[56px] h-[calc(100vh-56px)]"
      }`}
    >
      {/* ── Page header ───────────────────────────────────────────────────── */}
      <div className="border-b border-zinc-300 bg-white">
        <BaseWrapper>
          <div className="flex items-center justify-between py-[6px] gap-3 flex-wrap">
            <div className="flex justify-between">
              <h3 className="font-light">Work Orders Dashboard</h3>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <WeekBadge data={kpi?.weekSummary} />

              {/* ── Global filters ── */}
              <div className="flex items-center gap-2">
                <BaseSelect
                  options={clientOptions}
                  className="w-[160px] p-1"
                  required
                  labelShrink={false}
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder="All Clients"
                />
                <BaseSelect
                  options={teamOptions}
                  className="w-[150px] p-1"
                  required
                  labelShrink={false}
                  value={teamId}
                  onChange={(e) => setTeamId(e.target.value)}
                  placeholder="All Teams"
                />
                {activeFilterCount > 0 && (
                  <button
                    onClick={() => {
                      setClientId(ALL_VALUE);
                      setTeamId(ALL_VALUE);
                    }}
                    className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors whitespace-nowrap"
                    title="Clear filters"
                  >
                    <i className="fa-solid fa-xmark text-[9px]" />
                    Clear
                  </button>
                )}
              </div>

              <DateRangeShortcutPicker
                startDate={startDate}
                endDate={endDate}
                onChange={(start, end) => {
                  setStartDate(start);
                  setEndDate(end);
                }}
              />
            </div>
          </div>
        </BaseWrapper>
      </div>

      {/* ── Active filter badge ──────────────────────────────────────────── */}
      {activeFilterCount > 0 && (
        <div className="bg-blue-50 border-b border-blue-100 px-4 py-1.5">
          <BaseWrapper>
            <p className="text-[11px] text-blue-600 font-medium">
              <i className="fa-solid fa-filter mr-1.5" />
              Filtered by:{" "}
              {clientId && (
                <span className="font-semibold">
                  {clientOptions.find((o) => o.value === clientId)?.label}
                </span>
              )}
              {clientId && teamId && <span className="mx-1">·</span>}
              {teamId && (
                <span className="font-semibold">
                  {teamOptions.find((o) => o.value === teamId)?.label}
                </span>
              )}
            </p>
          </BaseWrapper>
        </div>
      )}

      <BaseWrapper className="py-5 flex flex-col gap-5">
        {/* ── 1. KPI cards ───────────────────────────────────────────────── */}
        <AnimateOnScroll delay={0}>
          <KpiCards data={kpi?.kpis} />
        </AnimateOnScroll>

        {/* ── 5. Status donut + Counter vs Requested + Busiest Days ───────── */}
        <AnimateOnScroll delay={60}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <TeamTrendsChart filterParams={filterParams} />

            <TargetProgress data={target} />
          </div>
        </AnimateOnScroll>

        <AnimateOnScroll delay={60}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card title="Work Order Status" subtitle={`Distribution · ${durationLabel}`}>
              <WorkOrderDonut data={analytics?.statusBreakdown} />
            </Card>
            <Card
              title="Counter vs Requested"
              subtitle={`Offer type breakdown · ${durationLabel}`}
              className="flex-1"
            >
              <RemoteOnsiteChart data={advanced?.counterVsRequested} />
            </Card>
            <Card title="Busiest Days" subtitle={`Work order volume by weekday · ${durationLabel}`}>
              <DayOfWeekChart data={breakdown?.dayOfWeek} />
            </Card>
          </div>
        </AnimateOnScroll>

        {/* ── 4. Monthly Completion Rate ───────────────────────────────────── */}
        <AnimateOnScroll delay={60}>
          <Card
            title="Monthly Completion Rate"
            subtitle={`${new Date().getFullYear()} — total vs completed work orders with % rate`}
          >
            <MonthlyCompletionChart data={advanced?.monthlyCompletionRate} />
          </Card>
        </AnimateOnScroll>

        {/* ── 6. Top categories ───────────────────────────────────────────── */}
        {breakdown?.topCategories?.length > 0 && (
          <AnimateOnScroll delay={60}>
            <Card
              title="Top Categories"
              subtitle={`Most active service categories · ${durationLabel}`}
            >
              <TopCategoriesChart data={breakdown.topCategories} />
            </Card>
          </AnimateOnScroll>
        )}

        {/* ── 7. World map ──────────────────────────────────────────────────── */}
        <AnimateOnScroll delay={60}>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-sm font-semibold text-gray-700">Work Orders by Country</h4>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  Geographic distribution · {durationLabel}
                </p>
              </div>
              {worldMap?.length > 0 && (
                <div className="text-[11px] text-gray-400 flex items-center gap-2">
                  <span>
                    <span className="font-semibold text-gray-700">{worldMap.length}</span>{" "}
                    {worldMap.length === 1 ? "country" : "countries"}
                  </span>
                  <span className="text-gray-300">·</span>
                  <span>
                    <span className="font-semibold text-gray-700">
                      {worldMap.reduce((sum, d) => sum + (d.count ?? 0), 0).toLocaleString()}
                    </span>{" "}
                    Work Orders
                  </span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <WorldMapChart data={worldMap} />
              </div>
              <div className="flex flex-col">
                <p className="text-[11px] text-gray-400 uppercase tracking-wide font-semibold mb-3">
                  Top Countries
                </p>
                <CountryRanking data={worldMap} />
              </div>
            </div>
          </div>
        </AnimateOnScroll>

        {/* ── 11b. Client Yearly Trend ─────────────────────────────────────── */}
        <AnimateOnScroll delay={60}>
          <ClientYearTrendChart filterParams={filterParams} />
        </AnimateOnScroll>

        <div className="h-4" />
      </BaseWrapper>
    </div>
  );
};

export default WorkOrdersAnalytics;
